import { EditorState } from '@tiptap/pm/state';
import { type Node as PMNode } from '@tiptap/pm/model';
import { AiToolkit, AiCaret } from '@tiptap-pro/ai-toolkit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type AnyExtension, Editor, getHTMLFromFragment } from '@tiptap/react';

import type { DependencyList } from 'react';
import type { EditorEvents, EditorOptions } from '@tiptap/react';

import { Selection } from '@tiptap/extensions';
import TextAlign from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extension-placeholder';
import { type TableOfContentData, TableOfContents } from '@tiptap/extension-table-of-contents';

import { Commands } from '@/extensions/commands';
import { Indent } from '@/extensions/indent/indent';
import { BlockNode } from '@/extensions/block-node';
import { MSOfficePaste } from '@/extensions/office';
import { CONTENT_SIZE_LIMIT_NOTIFICATION, ContentSizeLimit } from '@/extensions/content-size-limit';
import { SlashCommand } from '@/extensions/slash-command';
import { publishTocUpdate, TocEvents } from '@/extensions/toc-events';
import { ShiftClickSelection } from '@/extensions/shift-click-selection/shift-click-selection';

import { useApiRequestState } from '@/hooks/api';
import { getEditorHydratedReview, hasTerminalResolutionStarted } from '@/hooks/llm/editor-suggestion-config';
import { applyPendingReview, shouldKeepCachedEditorAlive } from '@/hooks/llm/use-llm-agent';

import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';
import { conditionalSpread } from '@flintmint/web-shared/utils/helper-utils';
import { computeToCState, focusEditorStart } from '@/utils/editor-utils';

import { coreExtensions, CustomDocument, StandardDocument } from '@flintmint/core-shared/extensions';
import { getCachedEditor, removeCachedEditor, setCachedEditor } from './editor-cache';

export const useBlockEditor = (editorOptions: EditorOptionsProps, deps: DependencyList = []) => {
  const {
    cacheable,
    autofocus,
    editorType,
    documentId,
    editorProps,
    llmRequestId,
    scrollParentId,
    placeholder = 'Start your note or type / to see more options',
  } = editorOptions;

  const { showToastNotification } = useToaster();

  const editorCacheKey = documentId ?? '';
  const runRequestId = llmRequestId ?? editorCacheKey;
  const { isLoading: isDocumentRequestLoading } = useApiRequestState(editorCacheKey);

  const isDocumentRequestLoadingRef = useRef(isDocumentRequestLoading);

  const getScroller = (editor?: Editor) => {
    if (scrollParentId) {
      const explicitScroller = document.getElementById(scrollParentId);
      if (explicitScroller) return explicitScroller;
    }

    const editorScroller = editor?.view?.dom?.closest('[data-editortype]');
    if (editorScroller instanceof HTMLElement) return editorScroller;

    return (document.querySelector('main') as HTMLElement | null) ?? document.body;
  };

  const updateTocIfHeading = (editor: Editor, selectedNode?: PMNode) => {
    if (selectedNode?.type.name !== 'heading') {
      return;
    }

    const id = selectedNode.attrs['data-toc-id'] as string | undefined;
    if (!id) {
      return;
    }

    setTimeout(() => {
      if (!editor.storage.documentInfo?.isComponentMounted) {
        return;
      }

      publishTocUpdate(
        editor,
        editor.storage.tocEvents.current.map((item) => ({
          ...item,
          isActive: item.id === id,
        }))
      );
    });
  };

  const getArtifacts = (editor: Editor) => {
    const sel = editor.state.selection;
    const { from, to } = sel;

    const selectedNode = sel.$head.node();
    const slice = editor.state.doc.slice(from, to);
    const html = getHTMLFromFragment(slice.content, editor.schema);

    const hasContent = !!editor.state.doc.textBetween(from, to).trim().length;

    return { from, to, slice, html, selectedNode, hasContent };
  };

  const onSelectionUpdate = useCallback(({ editor }: EditorEvents['selectionUpdate']) => {
    const tiptapEditor = editor as Editor;
    const artifacts = getArtifacts(tiptapEditor);
    updateTocIfHeading(tiptapEditor, artifacts.selectedNode);
  }, []);

  useEffect(() => {
    isDocumentRequestLoadingRef.current = isDocumentRequestLoading;
  }, [isDocumentRequestLoading]);

  const editor = useMemo(() => {
    const cached = editorCacheKey && cacheable ? getCachedEditor(editorCacheKey) : undefined;
    const shouldReuseCached = !!cached;

    if (shouldReuseCached) {
      return cached.editor;
    }

    let instance: Editor | null = null;
    const getInstanceScroller = () => getScroller(instance ?? undefined);

    const createdEditor = new Editor({
      autofocus: false,
      onCreate: (ctx) => {
        const tiptapEditor = ctx.editor as Editor;

        // A pending-review restore may have installed the review doc before this fires;
        // seeding over it would silently wipe the review's suggestions.
        const hydratedReview = getEditorHydratedReview(tiptapEditor);
        if (!hydratedReview) {
          tiptapEditor.commands.setContent(editorOptions.content);
        }

        // clears the editor history
        const newEditorState = EditorState.create({
          doc: tiptapEditor.state.doc,
          plugins: tiptapEditor.state.plugins,
          schema: tiptapEditor.state.schema,
          selection: tiptapEditor.state.selection,
        });

        tiptapEditor.view.updateState(newEditorState);

        // The history clear re-initializes every plugin's state, dropping the toolkit suggestions
        // a pre-create restore installed; re-apply the same payload over the cleared state —
        // unless terminal resolution already started, in which case the review is resolved and
        // re-applying would resurrect it (the seed above stays skipped so the resolved content survives).
        if (hydratedReview && !hasTerminalResolutionStarted(tiptapEditor)) {
          applyPendingReview(tiptapEditor, hydratedReview);
        }

        if (typeof autofocus === 'boolean' && autofocus) {
          autofocus && focusEditorStart(tiptapEditor);
        } else if (typeof autofocus === 'number') {
          tiptapEditor.commands.focus(autofocus);
        }
      },
      onSelectionUpdate,
      editorProps: {
        attributes: {
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
        },
        ...editorProps,
      },
      extensions: [
        AiToolkit,
        AiCaret.configure({
          user: {
            name: '',
            color: '#246bfd',
          },
          render: (user) => {
            const el = document.createElement('span');
            el.className = 'tiptap-ai-caret';
            el.style.borderColor = user.color;
            return el;
          },
        }),
        ...conditionalSpread([BlockNode], editorType === 'notes'),
        // Cached editors outlive this hook instance; `showToastNotification` only wraps sonner's
        // module-level `toast`, so the captured callback stays valid.
        ...conditionalSpread(
          [
            ContentSizeLimit.configure({
              onLimitReached: () => showToastNotification(CONTENT_SIZE_LIMIT_NOTIFICATION),
            }),
          ],
          SIZE_LIMITED_EDITOR_TYPES.includes(editorType)
        ),
        // Use CustomDocument for notes (supports block-instances), StandardDocument for others
        ...(editorType === 'notes' ? [CustomDocument] : [StandardDocument]),
        ...baseExtensions.filter((extension) => extension.name !== 'tableOfContents' && extension.name !== 'doc'),
        Commands.configure({
          documentId,
          suggestion: {
            ...SlashCommand({
              editorType,
              getScroller,
            }),
          },
        }),
        Selection,
        ShiftClickSelection,
        MSOfficePaste,
        TocEvents,
        TableOfContents.configure({
          onUpdate: (tocItems: TableOfContentData) => {
            const scroller = getInstanceScroller();

            // Check if the editor's React component is currently mounted. This prevents TOC updates for cached editors that are not visible
            const isComponentMounted = instance?.storage?.documentInfo?.isComponentMounted;

            if (!instance || !scroller || !isComponentMounted) {
              return;
            }

            publishTocUpdate(instance, computeToCState(scroller, tocItems));
          },
          scrollParent: getInstanceScroller,
        }),
        Placeholder.configure({
          placeholder,
          showOnlyWhenEditable: false,
        }),
      ],
      ...editorOptions,
    });
    instance = createdEditor;

    if (editorCacheKey && isDocumentRequestLoading && cacheable) {
      queueMicrotask(() => {
        setCachedEditor(editorCacheKey, { editor: createdEditor, documentId, llmRequestId });
      });
    }

    return createdEditor;
  }, [editorCacheKey, documentId, ...deps]);

  useEffect(() => {
    if (!editor) return;

    return () => {
      if (!editorCacheKey) return editor.destroy();

      const shouldKeepAlive = shouldKeepCachedEditorAlive({
        requestLoading: isDocumentRequestLoadingRef.current,
        runRequestId,
        agentActive: !!editor.storage?.documentInfo?.agentActive,
      });

      const cached = getCachedEditor(editorCacheKey);
      if (shouldKeepAlive && cacheable) {
        // Cached editors never get editor.unmount(); isInitialized stays true so @tiptap/react's
        // ReactRenderer uses flushSync when EditorContent remounts (invalid during React commit).
        // https://github.com/ueberdosis/tiptap/blob/96e86050873ca135541f2e439f9c29314b84cf35/packages/react/src/ReactRenderer.tsx#L184
        editor.isInitialized = false;
        setCachedEditor(editorCacheKey, { editor, documentId, llmRequestId });
      } else {
        editor.destroy();
        // Only evict the cache entry if it still belongs to this editor instance.
        // A stale cleanup (dep-change re-run) can fire after initializeAgentRequest has already
        // placed a newer editor in the cache — evicting it would kill the in-flight request.
        if (!cached || cached.editor === editor) {
          removeCachedEditor(editorCacheKey);
        }
      }
    };
  }, [editor, editorCacheKey, documentId, llmRequestId, runRequestId, cacheable]);

  return {
    editor,
  };
};

const SIZE_LIMITED_EDITOR_TYPES = ['notes', 'cortex', 'block'];

export const baseExtensions: AnyExtension[] = [
  ...coreExtensions,
  Indent,
  TextAlign.configure({
    types: ['heading', 'paragraph', 'listItem'],
  }),
];

interface EditorOptionsProps extends Partial<Omit<EditorOptions, 'content' | 'extensions'>> {
  content: string;
  cacheable?: boolean;
  editorReady?: boolean;
  documentId?: string;
  editorType: string;
  placeholder?: string;
  llmRequestId?: string;
  selectionBoxId: string;
  scrollParentId?: string;
  editorContainerId: string;
}
